/**
 * Main SPA Entry Point
 *
 * Single entry point for the entire application.
 * Initializes React, MSAL, and Router only ONCE per session.
 *
 * IMPORTANT: Version state is initialized BEFORE React renders.
 * This allows lazyWithReload (in routes.tsx) to check for version
 * mismatches before attempting to load lazy chunks, preventing
 * stale chunk 404 errors after deployments.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { MsalProvider } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig } from '@/shared';
import App from './App';
import { logger } from '@/shared/utils/logger';
import { initializeVersionState } from '@/shared/utils/versionState';
import { installGlobalErrorCapture } from '@/shared/utils/clientErrorCapture';
import './styles.css';

// Capture uncaught errors + unhandled rejections from the very start so any
// startup failures are buffered for the first heartbeat.
installGlobalErrorCapture();

// Headless-browser sign-in for test accounts (docs/AGENT_BROWSER.md). MODE is
// replaced at build time, so production bundles drop this import entirely.
if (import.meta.env.MODE !== 'production') {
  void import('@/shared/auth/agentSignIn').then((m) => m.installAgentSignIn());
}

// Initialize MSAL instance ONCE (never re-initialized)
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Application Initialization Flow:
 * 1. Initialize version state (parallel with MSAL)
 * 2. Initialize MSAL
 * 3. Handle redirect promise
 * 4. Render React app
 *
 * Version state initialization runs in parallel with MSAL init
 * to minimize startup delay. Even if version check completes after
 * React renders, it will be ready for subsequent route changes.
 */

// Start version state initialization early (non-blocking)
// This runs in parallel with MSAL initialization
const versionInitPromise = initializeVersionState().catch((error) => {
  // Non-fatal - app can still function without version state
  logger.warn('[VersionState] Initialization failed:', error);
});

// Register service worker early for PWA installability (non-blocking)
// Chrome requires a registered SW with fetch handler to fire beforeinstallprompt
// IMPORTANT: Must be served from root (/) for SW scope to cover manifest's start_url
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.register('/firebase-messaging-sw.js').catch((error) => {
    // Non-fatal - PWA install just won't be available
    logger.warn('[PWA] Service worker registration failed:', error);
  });
}

// Initialize MSAL and handle redirect promise
msalInstance.initialize().then(async () => {
  // Wait for version state to be initialized before rendering
  // This ensures checkVersionBeforeImport works on first navigation
  await versionInitPromise;

  // Handle redirect promise (required for redirect flow)
  msalInstance
    .handleRedirectPromise()
    .then(() => {
      // Render app after both MSAL and version state are ready
      ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
          <MsalProvider instance={msalInstance}>
            <App />
          </MsalProvider>
        </React.StrictMode>
      );
    })
    .catch((error) => {
      logger.error('[MSAL] Redirect promise error:', error);
    });
});
